/**
 * VideoUploadDialog — Vor dem Upload kann der Admin:
 *   • Start-/End-Zeit trimmen (Range-Slider),
 *   • einen Bildausschnitt wählen (Freiform oder 16:9 / 9:16 / 1:1),
 *   • einen Poster-Frame per Scrubber setzen,
 *   • zwischen 720p / 1080p wählen.
 * Anschließend wird das Original mit den Parametern an
 * `POST /api/upload-video` übermittelt, wo ffmpeg schneidet, croppt und in
 * MP4/H.264 mit +faststart transcodiert.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import {
  Play, Pause, Scissors, Crop as CropIcon, Image as ImageIcon,
  Loader2, RotateCcw, Sparkles, CheckCircle2, Volume2, VolumeX, Repeat, Type, Award,
  Music, Upload as UploadIcon, X as XIcon,
} from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECTS = [
  { key: 'free', label: 'Frei', ratio: null },
  { key: '16:9', label: '16:9', ratio: 16 / 9 },
  { key: '9:16', label: '9:16', ratio: 9 / 16 },
  { key: '1:1', label: '1:1', ratio: 1 },
  { key: '4:5', label: '4:5', ratio: 4 / 5 },
];

const formatTime = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const total = Math.max(0, Math.round(s * 10) / 10);
  const mm = Math.floor(total / 60);
  const ss = (total - mm * 60).toFixed(1).padStart(4, '0');
  return `${mm}:${ss}`;
};

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const VideoUploadDialog = ({ open, file, onClose, onUploaded }) => {
  const { settings } = useSettings();
  const brandLogoUrl = settings?.brand?.logo_url;
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [trim, setTrim] = useState([0, 0]);
  const [posterTime, setPosterTime] = useState(0);
  const [preset, setPreset] = useState('1080p');
  const [mute, setMute] = useState(false);
  const [smoothLoop, setSmoothLoop] = useState(false);
  // Text-Overlay
  const [overlayText, setOverlayText] = useState('');
  const [overlayTextPos, setOverlayTextPos] = useState('bottom-center');
  const [overlayTextColor, setOverlayTextColor] = useState('#ffffff');
  const [overlayTextBg, setOverlayTextBg] = useState(true);
  const [overlayTextSize, setOverlayTextSize] = useState(48);
  // Logo-Burn-in
  const [logoBurnin, setLogoBurnin] = useState(false);
  const [logoPos, setLogoPos] = useState('bottom-right');
  const [logoSizePct, setLogoSizePct] = useState(15);
  const [logoOpacity, setLogoOpacity] = useState(70);
  const [aspect, setAspect] = useState('free');
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 1, h: 1 });
  const [dragging, setDragging] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  // Phase 133.72 — Audiospur ersetzen (Jingle / eigene Musik)
  const [audioTrackUrl, setAudioTrackUrl] = useState('');     // URL der aktiven Audiospur
  const [audioTrackName, setAudioTrackName] = useState('');   // Anzeigename
  const [audioLibrary, setAudioLibrary] = useState([]);       // [{id,name,url,duration,size}]
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioSaveToLibrary, setAudioSaveToLibrary] = useState(true);
  const audioUploadRef = useRef(null);

  // Object-URL für die Preview generieren + wieder freigeben
  useEffect(() => {
    if (!file) { setVideoUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Phase 133.72 — Audio-Bibliothek beim Öffnen laden
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('adminToken') || '';
    setAudioLoading(true);
    axios.get(`${API}/admin/audio-library`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAudioLibrary(r.data?.tracks || []))
      .catch(() => { /* silent */ })
      .finally(() => setAudioLoading(false));
  }, [open]);

  const pickAudioFromLibrary = (track) => {
    if (!track) { setAudioTrackUrl(''); setAudioTrackName(''); return; }
    setAudioTrackUrl(track.url);
    setAudioTrackName(track.name || track.filename || 'Jingle');
  };

  const uploadNewAudio = async (file) => {
    if (!file) return;
    const token = localStorage.getItem('adminToken') || '';
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', file.name.replace(/\.[^.]+$/, ''));
    setAudioLoading(true);
    try {
      const r = await axios.post(`${API}/admin/audio-library`, fd, { headers: { Authorization: `Bearer ${token}` } });
      const track = r.data;
      setAudioTrackUrl(track.url);
      setAudioTrackName(track.name || track.filename || 'Jingle');
      if (audioSaveToLibrary) {
        setAudioLibrary((prev) => [track, ...prev]);
        toast.success(`Audiospur hochgeladen · "${track.name}" gespeichert`);
      } else {
        toast.success('Audiospur hochgeladen');
      }
    } catch (e) {
      toast.error('Audio-Upload fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAudioLoading(false);
      if (audioUploadRef.current) audioUploadRef.current.value = '';
    }
  };

  const removeAudioTrack = async (trackId) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm('Diesen Jingle wirklich aus der Bibliothek löschen?')) return;
    const token = localStorage.getItem('adminToken') || '';
    try {
      await axios.delete(`${API}/admin/audio-library/${trackId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAudioLibrary((prev) => prev.filter((t) => t.id !== trackId));
      toast.success('Jingle gelöscht');
    } catch (e) {
      toast.error('Löschen fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    }
  };

  // Metadaten laden
  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (!v) return;
    const d = v.duration || 0;
    setDuration(d);
    setTrim([0, d]);
    // Standard-Poster: 5 s nach Start (falls das Video ≥ 5 s lang ist,
    // sonst die Mitte). So bekommt man automatisch ein aussagekräftiges Standbild.
    setPosterTime(d >= 5 ? 5 : Math.max(0, d / 2));
    setCrop({ x: 0, y: 0, w: 1, h: 1 });
  };

  // Video-Playhead-Update
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = () => {
      setCurrent(v.currentTime);
      // Auto-Stop am Trim-Ende
      if (v.currentTime >= trim[1] - 0.05 && playing) {
        v.pause();
        v.currentTime = trim[0];
        setPlaying(false);
      }
    };
    v.addEventListener('timeupdate', onTime);
    return () => v.removeEventListener('timeupdate', onTime);
  }, [trim, playing]);

  const play = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.currentTime < trim[0] || v.currentTime > trim[1]) v.currentTime = trim[0];
    v.play();
    setPlaying(true);
  };
  const pause = () => {
    const v = videoRef.current;
    if (v) v.pause();
    setPlaying(false);
  };
  const seek = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = clamp(t, 0, duration || 0);
    setCurrent(v.currentTime);
  };

  const setAspectAndFit = (key) => {
    setAspect(key);
    const conf = ASPECTS.find((a) => a.key === key);
    if (!conf || !conf.ratio) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const targetRatio = conf.ratio;
    const videoRatio = v.videoWidth / v.videoHeight;
    // Wir cropen im 0..1-Space. Behalte volle Höhe/Breite, verkleinere die
    // Gegenachse so, dass das Ausgabe-Rechteck das gewählte Ratio hat.
    let w = 1, h = 1;
    if (targetRatio > videoRatio) {
      // Ziel ist breiter → volle Breite, Höhe reduzieren
      h = videoRatio / targetRatio;
    } else {
      w = targetRatio / videoRatio;
    }
    setCrop({
      x: (1 - w) / 2,
      y: (1 - h) / 2,
      w,
      h,
    });
  };

  // Crop-Rechteck via Mouse/Touch verschieben oder Kanten ziehen
  const onCropPointerDown = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragging({
      mode,
      startX: e.clientX,
      startY: e.clientY,
      rect,
      base: { ...crop },
    });
  };
  useEffect(() => {
    if (!dragging) return undefined;
    const onMove = (e) => {
      const { mode, startX, startY, rect, base } = dragging;
      const dx = (e.clientX - startX) / rect.width;
      const dy = (e.clientY - startY) / rect.height;
      let next = { ...base };
      if (mode === 'move') {
        next.x = clamp(base.x + dx, 0, 1 - base.w);
        next.y = clamp(base.y + dy, 0, 1 - base.h);
      } else if (mode === 'nw') {
        next.x = clamp(base.x + dx, 0, base.x + base.w - 0.1);
        next.y = clamp(base.y + dy, 0, base.y + base.h - 0.1);
        next.w = base.w - (next.x - base.x);
        next.h = base.h - (next.y - base.y);
      } else if (mode === 'ne') {
        next.y = clamp(base.y + dy, 0, base.y + base.h - 0.1);
        next.w = clamp(base.w + dx, 0.1, 1 - base.x);
        next.h = base.h - (next.y - base.y);
      } else if (mode === 'sw') {
        next.x = clamp(base.x + dx, 0, base.x + base.w - 0.1);
        next.w = base.w - (next.x - base.x);
        next.h = clamp(base.h + dy, 0.1, 1 - base.y);
      } else if (mode === 'se') {
        next.w = clamp(base.w + dx, 0.1, 1 - base.x);
        next.h = clamp(base.h + dy, 0.1, 1 - base.y);
      }
      setCrop(next);
    };
    const onUp = () => setDragging(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [dragging]);

  const resetAll = () => {
    setTrim([0, duration]);
    setPosterTime(duration >= 5 ? 5 : Math.max(0, duration / 2));
    setCrop({ x: 0, y: 0, w: 1, h: 1 });
    setAspect('free');
    setPreset('1080p');
    setMute(false);
    setSmoothLoop(false);
    setOverlayText('');
    setOverlayTextPos('bottom-center');
    setOverlayTextColor('#ffffff');
    setOverlayTextBg(true);
    setOverlayTextSize(48);
    setLogoBurnin(false);
    setLogoPos('bottom-right');
    setLogoSizePct(15);
    setLogoOpacity(70);
    setAudioTrackUrl('');
    setAudioTrackName('');
  };

  const startUpload = async () => {
    if (!file) return;
    const token = localStorage.getItem('adminToken');
    if (!token) { toast.error('Bitte melden Sie sich an'); return; }
    setUploading(true);
    setProgress(0);
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    try {
      // 1) Session anlegen
      const initRes = await axios.post(`${API}/upload-video/init`, {}, auth);
      const sessionId = initRes.data.session_id;
      if (!sessionId) throw new Error('Keine session_id erhalten');

      // 2) Datei in ~4 MB Chunks aufteilen und hochladen
      const CHUNK_SIZE = 4 * 1024 * 1024;
      const total = Math.ceil(file.size / CHUNK_SIZE) || 1;
      for (let i = 0; i < total; i += 1) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);
        const fd = new FormData();
        fd.append('session_id', sessionId);
        fd.append('index', String(i));
        fd.append('total', String(total));
        fd.append('chunk', blob, `chunk_${i}`);
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/upload-video/chunk`, fd, {
          ...auth,
          onUploadProgress: (ev) => {
            const chunkPct = ev.total ? ev.loaded / ev.total : 1;
            const overall = ((i + chunkPct) / total) * 88; // reserve 12% for encode
            setProgress(Math.round(overall));
          },
          timeout: 120000,
        });
      }

      // 3) Finalize + ffmpeg-Verarbeitung
      const cropChanged = crop.x > 0 || crop.y > 0 || crop.w < 1 || crop.h < 1;
      const finalizePayload = {
        session_id: sessionId,
        filename: file.name,
        content_type: file.type || 'video/mp4',
        trim_start: trim[0],
        trim_end: trim[1],
        preset,
        poster_time: posterTime,
        mute,
        smooth_loop: smoothLoop,
        ...(overlayText.trim() ? {
          overlay_text: overlayText.trim(),
          overlay_text_position: overlayTextPos,
          overlay_text_color: overlayTextColor,
          overlay_text_bg: overlayTextBg,
          overlay_text_size: overlayTextSize,
        } : {}),
        ...(logoBurnin && brandLogoUrl ? {
          logo_burnin: true,
          logo_position: logoPos,
          logo_size_pct: logoSizePct,
          logo_opacity: logoOpacity,
          logo_url: brandLogoUrl,
        } : {}),
        ...(audioTrackUrl && !mute ? { audio_track_url: audioTrackUrl } : {}),
        ...(cropChanged ? { crop_x: crop.x, crop_y: crop.y, crop_w: crop.w, crop_h: crop.h } : {}),
      };
      setProgress(90);
      const res = await axios.post(`${API}/upload-video/finalize`, finalizePayload, {
        ...auth,
        timeout: 240000,
      });
      setProgress(100);
      toast.success(
        `Video hochgeladen (${(res.data.optimized_size / 1024 / 1024).toFixed(1)} MB, `
        + `${res.data.saved_pct > 0 ? '−' : '+'}${Math.abs(res.data.saved_pct)}%)`,
      );
      onUploaded?.(res.data);
      onClose?.();
    } catch (e) {
      toast.error('Upload fehlgeschlagen: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const trimDur = Math.max(0, trim[1] - trim[0]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !uploading && onClose?.()}>
      <DialogContent className="max-w-3xl p-0 max-h-[92vh] flex flex-col">
        <DialogHeader className="px-6 pt-5 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-sky-500" /> Video optimieren &amp; hochladen
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-4 overflow-y-auto flex-1">
          {/* Video Preview + Crop overlay */}
          <div
            ref={containerRef}
            className="relative w-full bg-black rounded-xl overflow-hidden select-none"
            style={{ aspectRatio: '16 / 9' }}
          >
            {videoUrl && (
              <video
                ref={videoRef}
                src={videoUrl}
                onLoadedMetadata={onLoadedMetadata}
                onEnded={() => setPlaying(false)}
                className="absolute inset-0 w-full h-full object-contain bg-black"
                playsInline
                data-testid="video-editor-preview"
              />
            )}
            {/* Crop overlay (nur wenn video geladen) */}
            {duration > 0 && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Dimmed areas around the crop rect */}
                <div className="absolute inset-0 bg-black/40" />
                <div
                  className="absolute border-2 border-white/90 shadow-[0_0_0_9999px_rgba(0,0,0,0.4)] pointer-events-auto cursor-move"
                  style={{
                    left: `${crop.x * 100}%`,
                    top: `${crop.y * 100}%`,
                    width: `${crop.w * 100}%`,
                    height: `${crop.h * 100}%`,
                  }}
                  onPointerDown={(e) => onCropPointerDown(e, 'move')}
                  data-testid="crop-rect"
                >
                  {['nw', 'ne', 'sw', 'se'].map((corner) => (
                    <span
                      key={corner}
                      className={`absolute h-3 w-3 bg-white border border-sky-600 rounded-full cursor-${corner}-resize`}
                      style={{
                        left: corner.includes('w') ? -6 : 'auto',
                        right: corner.includes('e') ? -6 : 'auto',
                        top: corner.includes('n') ? -6 : 'auto',
                        bottom: corner.includes('s') ? -6 : 'auto',
                      }}
                      onPointerDown={(e) => onCropPointerDown(e, corner)}
                    />
                  ))}
                </div>
              </div>
            )}
            <div className="absolute bottom-2 left-2 flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="rounded-full h-8 px-3"
                onClick={playing ? pause : play}
                data-testid="video-editor-playpause"
              >
                {playing ? <Pause className="h-4 w-4 mr-1" /> : <Play className="h-4 w-4 mr-1" />}
                {playing ? 'Pause' : 'Abspielen'}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="rounded-full h-8 w-8 p-0"
                onClick={() => { if (videoRef.current) videoRef.current.muted = !videoRef.current.muted; }}
                data-testid="video-editor-preview-mute"
                title="Vorschau-Ton an/aus"
              >
                <Volume2 className="h-4 w-4" />
              </Button>
              <span className="text-xs text-white/90 font-medium bg-black/40 rounded-full px-2 py-1">
                {formatTime(current)} / {formatTime(duration)}
              </span>
            </div>
          </div>

          {/* Aspect Ratio Chips */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-neutral-500 inline-flex items-center gap-1">
              <CropIcon className="h-3.5 w-3.5" /> Ausschnitt:
            </span>
            {ASPECTS.map((a) => (
              <button
                type="button"
                key={a.key}
                onClick={() => setAspectAndFit(a.key)}
                className={`h-7 px-3 rounded-full text-xs font-semibold border transition-colors ${aspect === a.key ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-sky-300'}`}
                data-testid={`crop-aspect-${a.key}`}
              >
                {a.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => { setAspect('free'); setCrop({ x: 0, y: 0, w: 1, h: 1 }); }}
              className="h-7 px-3 rounded-full text-xs font-semibold text-neutral-600 hover:text-neutral-900"
            >
              <RotateCcw className="h-3.5 w-3.5 inline mr-1" /> Zurücksetzen
            </button>
          </div>

          {/* Trim */}
          <div className="space-y-2 rounded-2xl border border-neutral-200 p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-neutral-600 inline-flex items-center gap-1">
                <Scissors className="h-3.5 w-3.5" /> Anfang &amp; Ende
              </span>
              <span className="text-neutral-500">
                {formatTime(trim[0])} → {formatTime(trim[1])} · Dauer {formatTime(trimDur)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between text-[11px] text-neutral-500">
                  <span>Start</span><span>{formatTime(trim[0])}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={duration || 1}
                  step={0.1}
                  value={trim[0]}
                  onChange={(e) => {
                    const val = clamp(Number(e.target.value), 0, trim[1] - 0.2);
                    setTrim([val, trim[1]]);
                    seek(val);
                  }}
                  className="w-full accent-sky-600"
                  data-testid="video-editor-trim-start"
                />
              </div>
              <div>
                <div className="flex items-center justify-between text-[11px] text-neutral-500">
                  <span>Ende</span><span>{formatTime(trim[1])}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={duration || 1}
                  step={0.1}
                  value={trim[1]}
                  onChange={(e) => {
                    const val = clamp(Number(e.target.value), trim[0] + 0.2, duration || 1);
                    setTrim([trim[0], val]);
                    seek(val);
                  }}
                  className="w-full accent-sky-600"
                  data-testid="video-editor-trim-end"
                />
              </div>
            </div>
          </div>

          {/* Poster + Preset */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-2 rounded-2xl border border-neutral-200 p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-neutral-600 inline-flex items-center gap-1">
                  <ImageIcon className="h-3.5 w-3.5" /> Poster-Frame
                </span>
                <span className="text-neutral-500">{formatTime(posterTime)}</span>
              </div>
              <input
                type="range"
                min={trim[0]}
                max={Math.max(trim[1], trim[0] + 0.1)}
                step={0.1}
                value={posterTime}
                onChange={(e) => { setPosterTime(Number(e.target.value)); seek(Number(e.target.value)); }}
                className="w-full accent-sky-600"
                data-testid="video-editor-poster"
              />
              <p className="text-[11px] text-neutral-400">
                Standbild, das angezeigt wird bevor das Video startet.
              </p>
            </div>

            <div className="space-y-2 rounded-2xl border border-neutral-200 p-3">
              <Label className="text-xs font-semibold text-neutral-600">Qualität</Label>
              <div className="flex gap-2">
                {[
                  { key: '1080p', label: '1080p · ~2,8 Mbps' },
                  { key: '720p', label: '720p · ~1,4 Mbps' },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setPreset(opt.key)}
                    className={`flex-1 h-9 rounded-xl text-xs font-semibold border transition-colors ${preset === opt.key ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-sky-300'}`}
                    data-testid={`video-editor-preset-${opt.key}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-neutral-400">
                MP4/H.264 mit fast-start — sofort abspielbar aus dem CDN.
              </p>
            </div>
          </div>

          {/* Ton + Sanfter Loop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setMute((m) => !m)}
              disabled={uploading}
              className={`text-left rounded-2xl border p-3 transition-colors ${mute ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-sky-300'}`}
              data-testid="video-editor-mute-toggle"
            >
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="inline-flex items-center gap-1.5">
                  {mute ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
                  {mute ? 'Ohne Ton' : 'Mit Ton'}
                </span>
                <span className={`h-4 w-7 rounded-full relative transition-colors ${mute ? 'bg-white/30' : 'bg-neutral-200'}`}>
                  <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${mute ? 'left-3.5' : 'left-0.5'}`} />
                </span>
              </div>
              <p className={`text-[11px] mt-1 ${mute ? 'text-white/70' : 'text-neutral-400'}`}>
                {mute
                  ? 'Audiospur wird beim Kodieren entfernt (kleinere Datei).'
                  : 'Originalton bleibt erhalten (als AAC neu kodiert).'}
              </p>
            </button>

            <button
              type="button"
              onClick={() => setSmoothLoop((s) => !s)}
              disabled={uploading}
              className={`text-left rounded-2xl border p-3 transition-colors ${smoothLoop ? 'bg-sky-600 border-sky-600 text-white' : 'bg-white border-neutral-200 text-neutral-700 hover:border-sky-300'}`}
              data-testid="video-editor-smooth-loop-toggle"
            >
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className="inline-flex items-center gap-1.5">
                  <Repeat className="h-3.5 w-3.5" />
                  Sanfter Loop
                </span>
                <span className={`h-4 w-7 rounded-full relative transition-colors ${smoothLoop ? 'bg-white/30' : 'bg-neutral-200'}`}>
                  <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${smoothLoop ? 'left-3.5' : 'left-0.5'}`} />
                </span>
              </div>
              <p className={`text-[11px] mt-1 ${smoothLoop ? 'text-white/80' : 'text-neutral-400'}`}>
                Kurzes Ein-/Ausblenden an den Rändern — die Wiederholung wirkt
                weich statt hart. Empfohlen bei Clips ≤ 12 s.
              </p>
            </button>
          </div>

          {/* Phase 133.72 — Audiospur ersetzen (Jingle / eigene Musik) */}
          {!mute && (
            <div className="rounded-2xl border border-neutral-200 p-3 space-y-2.5" data-testid="video-editor-audio-track">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                  <Music className="h-3.5 w-3.5 text-violet-600" />
                  Audiospur ersetzen
                </span>
                <span className="text-[11px] text-neutral-500">
                  Ersetzt den Original-Ton komplett. Perfekt für Werbe-Clips mit Musik.
                </span>
              </div>

              {audioTrackUrl ? (
                <div className="flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2" data-testid="video-editor-audio-active">
                  <Music className="h-4 w-4 text-violet-700 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-violet-900 truncate">{audioTrackName}</p>
                    <audio controls src={audioTrackUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${audioTrackUrl}` : audioTrackUrl} className="w-full h-7 mt-1" />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setAudioTrackUrl(''); setAudioTrackName(''); }}
                    disabled={uploading}
                    className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-white hover:bg-red-50 border border-neutral-200 hover:border-red-300 transition-colors"
                    data-testid="video-editor-audio-clear"
                    title="Audiospur entfernen (Originalton bleibt)"
                  >
                    <XIcon className="h-3.5 w-3.5 text-red-600" />
                  </button>
                </div>
              ) : (
                <>
                  {audioLibrary.length > 0 && (
                    <div>
                      <Label className="text-[11px] text-neutral-500 mb-1 block">Aus Bibliothek wählen</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {audioLibrary.map((t) => (
                          <div key={t.id} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white hover:border-violet-300 transition-colors overflow-hidden" data-testid={`video-editor-audio-track-${t.id}`}>
                            <button
                              type="button"
                              onClick={() => pickAudioFromLibrary(t)}
                              disabled={uploading}
                              className="pl-3 pr-2 py-1 text-xs font-semibold text-neutral-700 hover:bg-violet-50 transition-colors inline-flex items-center gap-1.5"
                            >
                              <Music className="h-3 w-3 text-violet-500" />
                              {t.name}
                              {t.duration ? <span className="text-neutral-400">· {Math.round(t.duration)}s</span> : null}
                            </button>
                            <button
                              type="button"
                              onClick={() => removeAudioTrack(t.id)}
                              disabled={uploading}
                              className="h-7 w-7 inline-flex items-center justify-center text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors border-l border-neutral-100"
                              title={`"${t.name}" aus Bibliothek löschen`}
                              data-testid={`video-editor-audio-delete-${t.id}`}
                            >
                              <XIcon className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border text-xs font-semibold cursor-pointer transition-colors ${audioLoading ? 'opacity-60 cursor-wait' : 'border-neutral-200 bg-white hover:border-violet-400 text-neutral-700'}`} data-testid="video-editor-audio-upload-label">
                      {audioLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadIcon className="h-3.5 w-3.5" />}
                      Neue Audio-Datei hochladen
                      <input
                        ref={audioUploadRef}
                        type="file"
                        accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/ogg,audio/aac,audio/mp4,audio/m4a,audio/x-m4a"
                        className="hidden"
                        disabled={uploading || audioLoading}
                        onChange={(e) => uploadNewAudio(e.target.files?.[0])}
                        data-testid="video-editor-audio-upload"
                      />
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-neutral-600 select-none cursor-pointer">
                      <input
                        type="checkbox"
                        checked={audioSaveToLibrary}
                        onChange={(e) => setAudioSaveToLibrary(e.target.checked)}
                        disabled={uploading}
                        data-testid="video-editor-audio-save-library"
                      />
                      In Bibliothek speichern
                    </label>
                  </div>
                  {audioLibrary.length === 0 && !audioLoading && (
                    <p className="text-[11px] text-neutral-400 italic">
                      Noch keine Jingles in der Bibliothek. Lade eine Datei hoch — sie steht ab sofort für alle Videos zur Verfügung.
                    </p>
                  )}
                </>
              )}
            </div>
          )}
          <div className="rounded-2xl border border-neutral-200 p-3 space-y-3" data-testid="video-editor-text-overlay">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                <Type className="h-3.5 w-3.5" />
                Text ins Video einbrennen
              </span>
            </div>
            <Input
              value={overlayText}
              onChange={(e) => setOverlayText(e.target.value)}
              placeholder="z. B. Hüpfburg mieten in Schwerin"
              className="h-9 text-sm"
              disabled={uploading}
              data-testid="video-editor-text-input"
              maxLength={80}
            />
            {overlayText.trim() && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div>
                  <Label className="text-[11px] text-neutral-500">Position</Label>
                  <select
                    value={overlayTextPos}
                    onChange={(e) => setOverlayTextPos(e.target.value)}
                    className="w-full h-8 rounded-md border border-neutral-200 px-2 text-xs"
                    data-testid="video-editor-text-position"
                  >
                    <option value="top-left">Oben links</option>
                    <option value="top-center">Oben mittig</option>
                    <option value="top-right">Oben rechts</option>
                    <option value="middle-left">Mitte links</option>
                    <option value="middle-center">Mitte</option>
                    <option value="middle-right">Mitte rechts</option>
                    <option value="bottom-left">Unten links</option>
                    <option value="bottom-center">Unten mittig</option>
                    <option value="bottom-right">Unten rechts</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-neutral-500">Farbe</Label>
                  <input
                    type="color"
                    value={overlayTextColor}
                    onChange={(e) => setOverlayTextColor(e.target.value)}
                    className="w-full h-8 rounded-md border border-neutral-200"
                    data-testid="video-editor-text-color"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-neutral-500">Größe ({overlayTextSize} px)</Label>
                  <input
                    type="range"
                    min={20} max={120} step={2}
                    value={overlayTextSize}
                    onChange={(e) => setOverlayTextSize(Number(e.target.value))}
                    className="w-full accent-sky-600"
                    data-testid="video-editor-text-size"
                  />
                </div>
                <label className="flex items-center gap-2 mt-4 text-xs text-neutral-700 select-none">
                  <input
                    type="checkbox"
                    checked={overlayTextBg}
                    onChange={(e) => setOverlayTextBg(e.target.checked)}
                    className="accent-sky-600"
                    data-testid="video-editor-text-bg"
                  />
                  Dunkler Hintergrund
                </label>
              </div>
            )}
            <p className="text-[11px] text-neutral-400">Der Text wird beim Kodieren fest ins Video eingefügt und ist auf jedem Gerät sichtbar.</p>
          </div>

          {/* Logo-Burn-in */}
          <div className="rounded-2xl border border-neutral-200 p-3 space-y-3" data-testid="video-editor-logo-burnin">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-700">
                <Award className="h-3.5 w-3.5" />
                Logo ins Video einbrennen
              </span>
              <label className="inline-flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={logoBurnin}
                  onChange={(e) => setLogoBurnin(e.target.checked)}
                  disabled={uploading || !brandLogoUrl}
                  className="accent-sky-600"
                  data-testid="video-editor-logo-toggle"
                />
                aktiv
              </label>
            </div>
            {!brandLogoUrl && (
              <p className="text-[11px] text-amber-600">
                Kein Marken-Logo in den Branding-Einstellungen hinterlegt.
              </p>
            )}
            {logoBurnin && brandLogoUrl && (
              <>
                <div className="flex items-center gap-3">
                  <img src={brandLogoUrl} alt="" className="h-8 w-auto border border-neutral-200 rounded" />
                  <span className="text-[11px] text-neutral-500">Nutzt dein Marken-Logo (Branding-Einstellungen)</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <Label className="text-[11px] text-neutral-500">Position</Label>
                    <select
                      value={logoPos}
                      onChange={(e) => setLogoPos(e.target.value)}
                      className="w-full h-8 rounded-md border border-neutral-200 px-2 text-xs"
                      data-testid="video-editor-logo-position"
                    >
                      <option value="top-left">Oben links</option>
                      <option value="top-right">Oben rechts</option>
                      <option value="bottom-left">Unten links</option>
                      <option value="bottom-right">Unten rechts</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[11px] text-neutral-500">Größe ({logoSizePct}%)</Label>
                    <input
                      type="range" min={5} max={35} step={1}
                      value={logoSizePct}
                      onChange={(e) => setLogoSizePct(Number(e.target.value))}
                      className="w-full accent-sky-600"
                      data-testid="video-editor-logo-size"
                    />
                  </div>
                  <div>
                    <Label className="text-[11px] text-neutral-500">Deckkraft ({logoOpacity}%)</Label>
                    <input
                      type="range" min={20} max={100} step={5}
                      value={logoOpacity}
                      onChange={(e) => setLogoOpacity(Number(e.target.value))}
                      className="w-full accent-sky-600"
                      data-testid="video-editor-logo-opacity"
                    />
                  </div>
                </div>
              </>
            )}
            <p className="text-[11px] text-neutral-400">Anders als das Player-Wasserzeichen bleibt dies auch bei Download &amp; Repost erhalten.</p>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-2">
            <button
              type="button"
              onClick={resetAll}
              disabled={uploading}
              className="text-xs text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Alles zurücksetzen
            </button>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose} disabled={uploading}>
                Abbrechen
              </Button>
              <Button
                onClick={startUpload}
                disabled={uploading || !duration}
                className="rounded-full bg-sky-600 hover:bg-sky-700"
                data-testid="video-editor-upload"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    {progress < 88 ? `Upload ${progress}%` : progress < 100 ? 'Encodiere …' : 'Fertig'}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Optimieren &amp; hochladen
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoUploadDialog;
